/**
 * Technician-app-specific formatters that don't belong in
 * `@vollgas/shared/utils` — either because nothing else needs them
 * (`formatPhone`, `noteAuthorLabel`) or because the shared date/money/
 * duration formatters cover the general case and this one is a deliberate
 * workaround for an ICU quirk (`formatKm`).
 *
 * Everything else that used to live here — `formatMoneyEUR`, `formatDateLong`,
 * `formatDuration`, plus the dead `formatTimeHHMM` / `formatRelativePause` —
 * moved to `@vollgas/shared/utils`, which is Vienna-zoned and locale-correct
 * where this file's originals were `en-US`-flavoured and un-zoned.
 */
import { localeFor, type Lang } from '@vollgas/shared/utils'

/**
 * Mileage, grouped: `84.250 km`. Forces `.` as the thousands separator
 * regardless of the local ICU build — recent CLDR data renders `de-AT`
 * grouping with a narrow no-break space (`84 250`) rather than a period,
 * which doesn't match this app's copy. `de-DE` still groups with a plain
 * period, so it's used here as a formatting trick only; the locale
 * actually shown is still governed by `lang`, not by this implementation
 * detail.
 */
export function formatKm(km: number, lang: Lang = 'de'): string {
  if (lang === 'de') {
    const formatted = new Intl.NumberFormat('de-DE').format(km).replace(/\s/g, '.')
    return `${formatted} km`
  }
  return `${new Intl.NumberFormat(localeFor('en')).format(km)} km`
}

export function formatPhone(raw: string): string {
  return raw.trim()
}

/**
 * Who wrote a task note — the "You" label for your own, the author's name
 * for anyone else's, an em dash when the note has no author (SYSTEM or an
 * erased user). Notes render in four places; the attribution rule is one.
 */
/**
 * When a job note was written: the clock alone for today's notes, day +
 * clock for older ones — the advisor often writes the intake note the
 * evening before. `at` is the server's HH:mm in shop time; the day comes
 * from the instant.
 */
export function noteStamp(
  note: { readonly at: string; readonly createdAt?: string | null },
  lang: Lang = 'de',
  now: Date = new Date(),
): string {
  if (!note.createdAt) return note.at
  const d = new Date(note.createdAt)
  if (Number.isNaN(d.getTime())) return note.at
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate()
  if (sameDay) return note.at
  const day = new Intl.DateTimeFormat(localeFor(lang), { day: '2-digit', month: '2-digit' }).format(d)
  return `${day} ${note.at}`
}

export function noteAuthorLabel(
  note: { readonly mine: boolean; readonly authorName: string | null },
  youLabel: string,
): string {
  if (note.mine) return youLabel
  return note.authorName ?? '—'
}
